import React, { useEffect, useMemo, useState } from "react";
import * as t from "io-ts";
import * as FirstPartyFetchResponse from "../../../../../domain/models/FirstPartyFetchResponse";
import { getJson } from "../../../wrappers/restRequest";
import { introducerPortalBasePath } from "../getIntroducerSlugFromUrl";
import { getAsIntroducerIdFromUrl, withAsIntroducerId } from "../asIntroducerId";
import * as fetchWrapper from "../../../wrappers/fetch";
import { getApiUrl } from "../../../wrappers/apiUrl";
import { Background } from "../../../components/BuildingBlocks/Background";
import { Padding } from "../../../components/BuildingBlocks/Padding";
import { SpacingColumn } from "../../../components/BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../../components/BuildingBlocks/SpacingRow";
import { Flex } from "../../../components/BuildingBlocks/Flex";
import { FontRobotoSlab } from "../../../components/BuildingBlocks/FontRobotoSlab";
import { FontQuicksand } from "../../../components/BuildingBlocks/FontQuicksand";
import { FontSize } from "../../../components/BuildingBlocks/FontSize";
import { TextColor } from "../../../components/BuildingBlocks/TextColor";
import { TextAlign } from "../../../components/BuildingBlocks/TextAlign";
import { TextCenter } from "../../../components/TextCenter/TextCenter";
import { CursorPointer } from "../../../components/CursorPointer/CursorPointer";
import { Uppercase } from "../../../components/Uppercase/Uppercase";
import { WeightMedium } from "../../../components/WeightMedium/WeightMedium";
import { WeightBold } from "../../../components/WeightBold/WeightBold";
import { WeightSemiBold } from "../../../components/WeightSemiBold/WeightSemiBold";
import { FrontColors } from "../../../models/FrontColors";
import { FrontFontSizes } from "../../../models/FrontFontSizes";
import { FrontSpacing } from "../../../models/FrontSpacing";
import { FPSimpleLogo } from "../../../components/FrontProbate/Simple/FPSimpleLogo/FPSimpleLogo";
import { FPLandingTitle } from "../../../components/FrontProbate/Simple/FPLandingTitle/FPLandingTitle";
import { MaxWidth } from "../../../components/BuildingBlocks/MaxWidth";

const BucketCodec = t.union([
    t.literal("property_report"),
    t.literal("quote"),
    t.literal("instructed"),
    t.literal("ready_for_exchange"),
    t.literal("exchanged"),
    t.literal("completed"),
    t.literal("closed_unsuccessful"),
]);
type TBucket = t.TypeOf<typeof BucketCodec>;

const AddressCodec = t.type({
    building_name: t.union([t.string, t.null]),
    building_number: t.union([t.string, t.null]),
    sub_building_name: t.union([t.string, t.null]),
    sub_building_number: t.union([t.string, t.null]),
    street_name: t.union([t.string, t.null]),
    city_town: t.union([t.string, t.null]),
    locality: t.union([t.string, t.null]),
    county: t.union([t.string, t.null]),
    country: t.union([t.string, t.null]),
    postcode: t.union([t.string, t.null]),
});
type TAddress = t.TypeOf<typeof AddressCodec>;

const PartyCodec = t.type({
    first_name: t.union([t.string, t.null]),
    last_name: t.union([t.string, t.null]),
    user_type: t.string,
});
type TParty = t.TypeOf<typeof PartyCodec>;

const CaseDetailCodec = t.union([
    t.null,
    t.type({
        id: t.string,
        bucket: BucketCodec,
        address: t.union([AddressCodec, t.null]),
        asking_price_main_currency: t.union([t.number, t.null]),
        live_date: t.union([t.string, t.null]),
        days_on_market: t.union([t.number, t.null]),
        offers_count_last_30d: t.number,
        viewings_count_last_30d: t.number,
        dead_time_days: t.number,
        is_stalled: t.boolean,
        instructed_at: t.union([t.string, t.null]),
        expected_exchange_date: t.union([t.string, t.null]),
        expected_completion_date: t.union([t.string, t.null]),
        completed_at: t.union([t.string, t.null]),
        parties: t.array(PartyCodec),
    }),
]);
type TCaseDetail = t.TypeOf<typeof CaseDetailCodec>;

const SummaryOwnerCodec = t.union([
    t.literal("client"),
    t.literal("other_side"),
    t.literal("third_party"),
    t.literal("us"),
]);
type TSummaryOwner = t.TypeOf<typeof SummaryOwnerCodec>;

const SummaryWaitingOnItemCodec = t.type({
    owner: SummaryOwnerCodec,
    label: t.string,
    detail: t.union([t.string, t.null]),
    due_date: t.union([t.string, t.null]),
    assignee_name: t.union([t.string, t.null]),
});

const SummaryWhatsNextItemCodec = t.type({
    owner: t.union([SummaryOwnerCodec, t.null]),
    action: t.string,
});
type TSummaryWhatsNextItem = t.TypeOf<typeof SummaryWhatsNextItemCodec>;

const QuickSummaryCodec = t.union([
    t.null,
    t.type({
        headline: t.string,
        predicted_completion_month: t.union([t.string, t.null]),
        predicted_completion_confidence: t.union([t.literal("low"), t.literal("medium"), t.literal("high"), t.null]),
        whats_next: t.array(t.string),
        blocking: t.array(t.string),
        waiting_on: t.array(SummaryWaitingOnItemCodec),
        whats_next_v2: t.union([t.array(SummaryWhatsNextItemCodec), t.null]),
        is_stale: t.boolean,
        generated_at: t.string,
    }),
]);
type TQuickSummary = t.TypeOf<typeof QuickSummaryCodec>;

const summaryOwnerDisplay: Record<TSummaryOwner, string> = {
    client: "The client",
    other_side: "The other side's solicitor",
    third_party: "A third party",
    us: "Sail Legal",
};

const summaryOwnerOrder: Array<TSummaryOwner> = ["client", "other_side", "third_party", "us"];

const BUCKET_LABELS: Record<TBucket, string> = {
    property_report: "Property Report",
    quote: "Quote sent",
    instructed: "Instructed",
    ready_for_exchange: "Ready to exchange",
    exchanged: "Exchanged",
    completed: "Completed",
    closed_unsuccessful: "Closed",
};

const BUCKET_ORDER: ReadonlyArray<TBucket> = [
    "property_report",
    "quote",
    "instructed",
    "ready_for_exchange",
    "exchanged",
    "completed",
];

const formatAddress = (address: TAddress | null): string => {
    if (!address) return "Unknown address";
    const buildingPart = [address.building_number, address.building_name].filter((p): p is string => !!p).join(" ");
    const parts = [buildingPart, address.street_name, address.city_town, address.postcode].filter((p): p is string => !!p && p.trim().length > 0);
    return parts.length === 0 ? "Unknown address" : parts.join(", ");
};

const formatPounds = (n: number | null): string => {
    if (n === null) return "—";
    return `£${n.toLocaleString("en-GB")}`;
};

const formatIsoDate = (iso: string | null): string => {
    if (iso === null) return "—";
    const ymd = iso.slice(0, 10);
    const [y, m, d] = ymd.split("-");
    const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][Number(m) - 1] ?? m;
    return `${Number(d)} ${month} ${y}`;
};

const formatPartyName = (p: TParty): string =>
    [p.first_name, p.last_name].filter((s): s is string => !!s && s.trim().length > 0).join(" ").trim() || "Unnamed";

const PurpleShell = (props: React.PropsWithChildren<{}>): JSX.Element => (
    <Background color={FrontColors.PROBATE_0} minHeight="100vh">
        <Padding type="custom" spacing={`${FrontSpacing.LARGE_3} ${FrontSpacing.MEDIUM_1}`}>
            <div style={{ maxWidth: "1180px", width: "100%", marginLeft: "auto", marginRight: "auto" }}>
                {props.children}
            </div>
        </Padding>
    </Background>
);

const StateHero = (props: { title: string; body: React.ReactNode }): JSX.Element => (
    <Flex justifyContent="center" alignItems="center" minHeight="60vh">
        <SpacingColumn spacing={FrontSpacing.LARGE_1} justifyContent="center">
            <Flex justifyContent="center">
                <FPSimpleLogo width="60px" />
            </Flex>
            <Flex justifyContent="center">
                <MaxWidth width="560px">
                    <FPLandingTitle size={48} color={FrontColors.NEUTRAL_18} align="center">
                        {props.title}
                    </FPLandingTitle>
                </MaxWidth>
            </Flex>
            <Flex justifyContent="center">
                <MaxWidth width="480px">
                    <FontQuicksand>
                        <FontSize size={FrontFontSizes.MED} lineHeight={1.45}>
                            <WeightSemiBold>
                                <TextColor color={FrontColors.NEUTRAL_18}>
                                    <TextAlign position="center">
                                        {props.body}
                                    </TextAlign>
                                </TextColor>
                            </WeightSemiBold>
                        </FontSize>
                    </FontQuicksand>
                </MaxWidth>
            </Flex>
        </SpacingColumn>
    </Flex>
);

const SectionLabel = (props: { children: React.ReactNode }): JSX.Element => (
    <FontRobotoSlab>
        <FontSize size={FrontFontSizes.X_SMALL}>
            <TextColor color={FrontColors.NEUTRAL_18}>
                <Uppercase>
                    <WeightMedium>{props.children}</WeightMedium>
                </Uppercase>
            </TextColor>
        </FontSize>
    </FontRobotoSlab>
);

const Card = (props: React.PropsWithChildren<{}>): JSX.Element => (
    <Background color={FrontColors.NEUTRAL_18} borderRadius="5px">
        <Padding type="custom" spacing={`${FrontSpacing.MEDIUM_1} ${FrontSpacing.MEDIUM_1}`}>
            {props.children}
        </Padding>
    </Background>
);

const StatTile = (props: { label: string; value: React.ReactNode; helper?: React.ReactNode }): JSX.Element => (
    <Card>
        <SpacingColumn spacing={FrontSpacing.SMALL_2}>
            <FontQuicksand>
                <FontSize size={FrontFontSizes.X_SMALL} lineHeight="1.2">
                    <TextColor color={FrontColors.NEUTRAL_INK_LIGHT}>
                        <Uppercase>
                            <WeightMedium>{props.label}</WeightMedium>
                        </Uppercase>
                    </TextColor>
                </FontSize>
            </FontQuicksand>
            <FontRobotoSlab>
                <FontSize size={FrontFontSizes.LARGE} lineHeight="1.1">
                    <TextColor color={FrontColors.NEUTRAL_INK}>
                        <WeightMedium>{props.value}</WeightMedium>
                    </TextColor>
                </FontSize>
            </FontRobotoSlab>
            {props.helper !== undefined && (
                <FontQuicksand>
                    <FontSize size={FrontFontSizes.X_SMALL} lineHeight="1.3">
                        <TextColor color={FrontColors.NEUTRAL_INK_LIGHT}>
                            {props.helper}
                        </TextColor>
                    </FontSize>
                </FontQuicksand>
            )}
        </SpacingColumn>
    </Card>
);

const PipelineStrip = (props: { current: TBucket }): JSX.Element => {
    const currentIndex = BUCKET_ORDER.indexOf(props.current);
    const isClosed = props.current === "closed_unsuccessful";
    return (
        <SpacingRow spacing={FrontSpacing.SMALL_2} childSize="even-fill">
            {BUCKET_ORDER.map((bucket, i) => {
                const reached = !isClosed && i <= currentIndex;
                const isActive = !isClosed && i === currentIndex;
                return (
                    <Background
                        key={bucket}
                        color={reached ? FrontColors.NEUTRAL_18 : "transparent"}
                        borderRadius="5px"
                        borderDefinition={`solid 1px ${FrontColors.NEUTRAL_18}`}
                    >
                        <Padding type="custom" spacing={`${FrontSpacing.SMALL_3} ${FrontSpacing.SMALL_2}`}>
                            <TextCenter>
                                <FontQuicksand>
                                    <FontSize size={FrontFontSizes.X_SMALL} lineHeight="1.2">
                                        <TextColor color={reached ? FrontColors.NEUTRAL_INK : FrontColors.NEUTRAL_18}>
                                            <Uppercase>
                                                {isActive ? <WeightBold>{BUCKET_LABELS[bucket]}</WeightBold> : <WeightMedium>{BUCKET_LABELS[bucket]}</WeightMedium>}
                                            </Uppercase>
                                        </TextColor>
                                    </FontSize>
                                </FontQuicksand>
                            </TextCenter>
                        </Padding>
                    </Background>
                );
            })}
        </SpacingRow>
    );
};

type TUploadDocumentType =
    | "will"
    | "grant_of_probate"
    | "letters_of_admin"
    | "death_certificate"
    | "lasting_power_of_attorney"
    | "other";

const UPLOAD_DOCUMENT_LABELS: Record<TUploadDocumentType, string> = {
    will: "Will",
    grant_of_probate: "Grant of probate",
    letters_of_admin: "Letter of administration",
    death_certificate: "Death certificate",
    lasting_power_of_attorney: "Lasting power of attorney",
    other: "Other",
};

const getFileExtension = (name: string): string => {
    const dot = name.lastIndexOf(".");
    if (dot < 0 || dot === name.length - 1) return "";
    return name.slice(dot + 1).toLowerCase();
};

const DocumentUploader = (props: { caseId: string; disabled: boolean }): JSX.Element => {
    const [file, setFile] = useState<File | null>(null);
    const [documentType, setDocumentType] = useState<TUploadDocumentType>("will");
    const [additionalName, setAdditionalName] = useState<string>("");
    const [status, setStatus] = useState<"idle" | "uploading" | "success" | "error">("idle");

    const submitDisabled =
        file === null
        || status === "uploading"
        || (documentType === "other" && additionalName.trim() === "");

    const submit = (): void => {
        if (submitDisabled) return;
        setStatus("uploading");
        fetchWrapper.file({
            requestParams: {
                url: `${getApiUrl()}/v1/introducer/cases/${props.caseId}/documents`,
                method: "POST",
                body: file,
                headers: {
                    "Content-Type": "application/octet-stream",
                    "x-sail-name": file.name,
                    "x-sail-mime-type": file.type || "application/octet-stream",
                    "x-sail-file-extension": getFileExtension(file.name),
                    "x-sail-document-type": documentType,
                    "x-sail-additional-name": additionalName,
                },
            },
        })().then((resp) => {
            if (resp.tag === FirstPartyFetchResponse.constants.STATUS_204
                || resp.tag === FirstPartyFetchResponse.constants.STATUS_2XX) {
                setStatus("success");
                setFile(null);
                setAdditionalName("");
            } else {
                setStatus("error");
            }
        });
    };

    if (props.disabled) {
        return (
            <Background color={FrontColors.NEUTRAL_18} borderRadius="5px">
                <Padding type="custom" spacing={`${FrontSpacing.MEDIUM_1} ${FrontSpacing.MEDIUM_1}`}>
                    <FontQuicksand>
                        <FontSize size={FrontFontSizes.SMALL} lineHeight="1.4">
                            <TextColor color={FrontColors.NEUTRAL_INK_LIGHT}>
                                Document upload is disabled in staff preview mode. Open the customer&apos;s real session to upload.
                            </TextColor>
                        </FontSize>
                    </FontQuicksand>
                </Padding>
            </Background>
        );
    }

    return (
        <Background color={FrontColors.NEUTRAL_18} borderRadius="5px">
            <Padding type="custom" spacing={`${FrontSpacing.MEDIUM_1} ${FrontSpacing.MEDIUM_1}`}>
                <SpacingColumn spacing={FrontSpacing.SMALL_3}>
                    <FontQuicksand>
                        <FontSize size={FrontFontSizes.SMALL} lineHeight="1.4">
                            <TextColor color={FrontColors.NEUTRAL_INK_LIGHT}>
                                Upload supporting documents (will, grant of probate, death certificate, LPA). The Sail Legal team will be notified to review.
                            </TextColor>
                        </FontSize>
                    </FontQuicksand>

                    <div>
                        <select
                            value={documentType}
                            onChange={(e) => {
                                const nextType = e.target.value as TUploadDocumentType;
                                setDocumentType(nextType);
                                if (nextType !== "other") {
                                    setAdditionalName("");
                                }
                            }}
                            disabled={status === "uploading"}
                            className="document-uploader__select"
                        >
                            {(Object.keys(UPLOAD_DOCUMENT_LABELS) as TUploadDocumentType[]).map((k) => (
                                <option key={k} value={k}>{UPLOAD_DOCUMENT_LABELS[k]}</option>
                            ))}
                        </select>
                    </div>

                    {documentType === "other" && (
                        <input
                            type="text"
                            placeholder="What is this document? (e.g. passport)"
                            value={additionalName}
                            onChange={(e) => setAdditionalName(e.target.value)}
                            disabled={status === "uploading"}
                            className="document-uploader__additional-name"
                        />
                    )}

                    <input
                        type="file"
                        onChange={(e) => {
                            setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
                            setStatus("idle");
                        }}
                        disabled={status === "uploading"}
                        className="document-uploader__file-input"
                    />

                    {(() => {
                        const button = (
                            <Background
                                color={submitDisabled ? FrontColors.NEUTRAL_11 : FrontColors.PROBATE_0}
                                borderRadius="5px"
                            >
                                <Padding type="custom" spacing={`${FrontSpacing.SMALL_3} ${FrontSpacing.MEDIUM_1}`}>
                                    <TextCenter>
                                        <FontRobotoSlab>
                                            <FontSize size={FrontFontSizes.SMALL}>
                                                <TextColor color={FrontColors.NEUTRAL_18}>
                                                    <WeightBold>
                                                        {status === "uploading"
                                                            ? "Uploading…"
                                                            : status === "success"
                                                            ? "Uploaded. Send another."
                                                            : "Send document"}
                                                    </WeightBold>
                                                </TextColor>
                                            </FontSize>
                                        </FontRobotoSlab>
                                    </TextCenter>
                                </Padding>
                            </Background>
                        );
                        return submitDisabled ? button : <CursorPointer onClick={submit}>{button}</CursorPointer>;
                    })()}

                    {status === "error" && (
                        <FontQuicksand>
                            <FontSize size={FrontFontSizes.X_SMALL} lineHeight="1.4">
                                <TextColor color={FrontColors.PERTINENT_CORAL_4}>
                                    <WeightBold>Couldn&apos;t upload that document.</WeightBold> Refresh and try again, or contact your Sail Legal contact.
                                </TextColor>
                            </FontSize>
                        </FontQuicksand>
                    )}
                </SpacingColumn>
            </Padding>
        </Background>
    );
};

const getCaseIdFromUrl = (): string | null => {
    const match = window.location.pathname.match(/^\/[^/]+\/cases\/([^/]+)\/?$/);
    return match ? match[1] : null;
};

const redirectTo = (path: string): void => {
    window.location.href = path;
};

const SelfPreviewCodec = t.union([
    t.null,
    t.type({
        introducer: t.type({ name: t.string }),
        is_impersonating: t.boolean,
    }),
]);

export const IntroducerPortalCaseDetailPage = (): JSX.Element => {
    const caseId = useMemo(getCaseIdFromUrl, []);
    const asIntroducerId = useMemo(getAsIntroducerIdFromUrl, []);

    const [detail, setDetail] = useState<TCaseDetail>(null);
    const [summary, setSummary] = useState<TQuickSummary>(null);
    const [summaryLoaded, setSummaryLoaded] = useState<boolean>(false);
    const [loadStatus, setLoadStatus] = useState<"loading" | "ready" | "unauthorised" | "not_found" | "error">("loading");
    const [previewBannerName, setPreviewBannerName] = useState<string | null>(null);

    useEffect(() => {
        if (!asIntroducerId) return;
        let cancelled = false;
        getJson({
            url: withAsIntroducerId("/v1/introducer/me", asIntroducerId),
            dataCodec: SelfPreviewCodec,
            defaultData: null,
        })().then((resp) => {
            if (cancelled) return;
            if (resp.tag === FirstPartyFetchResponse.constants.STATUS_2XX
                && resp.response.data
                && resp.response.data.is_impersonating) {
                setPreviewBannerName(resp.response.data.introducer.name);
            }
        });
        return () => { cancelled = true; };
    }, [asIntroducerId]);

    useEffect(() => {
        if (!caseId) {
            setLoadStatus("not_found");
            return;
        }
        let cancelled = false;
        setLoadStatus("loading");

        getJson({
            url: withAsIntroducerId(`/v1/introducer/cases/${caseId}`, asIntroducerId),
            dataCodec: CaseDetailCodec,
            defaultData: null,
        })().then((resp) => {
            if (cancelled) return;
            if (resp.tag === FirstPartyFetchResponse.constants.STATUS_2XX) {
                setDetail(resp.response.data);
                setLoadStatus("ready");
                return;
            }
            if (resp.tag === FirstPartyFetchResponse.constants.STATUS_401) {
                setLoadStatus("unauthorised");
                return;
            }
            if (resp.tag === FirstPartyFetchResponse.constants.STATUS_404) {
                setLoadStatus("not_found");
                return;
            }
            setLoadStatus("error");
        });

        return () => {
            cancelled = true;
        };
    }, [caseId]);

    useEffect(() => {
        if (!caseId || loadStatus !== "ready") return;
        let cancelled = false;
        getJson({
            url: withAsIntroducerId(`/v1/introducer/cases/${caseId}/quick-summary`, asIntroducerId),
            dataCodec: QuickSummaryCodec,
            defaultData: null,
        })().then((resp) => {
            if (cancelled) return;
            setSummaryLoaded(true);
            if (resp.tag === FirstPartyFetchResponse.constants.STATUS_2XX) {
                setSummary(resp.response.data);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [caseId, loadStatus]);

    useEffect(() => {
        if (loadStatus === "unauthorised") {
            redirectTo(`${introducerPortalBasePath()}/login`);
        }
    }, [loadStatus]);

    if (loadStatus === "loading" || loadStatus === "unauthorised") {
        return (
            <PurpleShell>
                <StateHero
                    title={loadStatus === "unauthorised" ? "Taking you to sign-in." : "Loading this case."}
                    body={loadStatus === "unauthorised"
                        ? <>One moment — we couldn&apos;t verify your session.</>
                        : <>Pulling together the latest progress and conveyancing detail.</>}
                />
            </PurpleShell>
        );
    }

    if (loadStatus === "not_found") {
        return (
            <PurpleShell>
                <StateHero
                    title="Case not found."
                    body={<>We couldn&apos;t find this case in your portfolio. <CursorPointer onClick={() => redirectTo(withAsIntroducerId(`${introducerPortalBasePath()}/dashboard`, asIntroducerId))}><WeightBold>Back to dashboard</WeightBold></CursorPointer>.</>}
                />
            </PurpleShell>
        );
    }

    if (loadStatus === "error" || !detail) {
        return (
            <PurpleShell>
                <StateHero
                    title="Something's off."
                    body={<>We couldn&apos;t load this case. Refresh the page — if it keeps happening, contact your <WeightBold>Sail Legal</WeightBold> contact.</>}
                />
            </PurpleShell>
        );
    }

    return (
        <PurpleShell>
            <SpacingColumn spacing={FrontSpacing.MEDIUM_2}>
                {previewBannerName !== null && (
                    <Background color={FrontColors.PERTINENT_CORAL_4} borderRadius="5px">
                        <Padding type="custom" spacing={`${FrontSpacing.SMALL_3} ${FrontSpacing.MEDIUM_1}`}>
                            <FontQuicksand>
                                <FontSize size={FrontFontSizes.SMALL} lineHeight="1.4">
                                    <TextColor color={FrontColors.NEUTRAL_18}>
                                        <WeightBold>Sail staff preview</WeightBold> — viewing {previewBannerName}&apos;s portal. Activity is logged to the introducer audit log.
                                    </TextColor>
                                </FontSize>
                            </FontQuicksand>
                        </Padding>
                    </Background>
                )}
                {/* Back link */}
                <CursorPointer onClick={() => redirectTo(withAsIntroducerId(`${introducerPortalBasePath()}/dashboard`, asIntroducerId))}>
                    <FontQuicksand>
                        <FontSize size={FrontFontSizes.X_SMALL} lineHeight="1.2">
                            <TextColor color={FrontColors.NEUTRAL_18}>
                                <WeightMedium>← Back to portfolio</WeightMedium>
                            </TextColor>
                        </FontSize>
                    </FontQuicksand>
                </CursorPointer>

                {/* Header */}
                <SpacingColumn spacing={FrontSpacing.SMALL_2}>
                    <TextCenter>
                        <FPSimpleLogo width="48px" />
                    </TextCenter>
                    <TextCenter>
                        <FontRobotoSlab>
                            <FontSize size={FrontFontSizes.BIG} lineHeight="1.1">
                                <TextColor color={FrontColors.NEUTRAL_18}>
                                    <WeightMedium>{formatAddress(detail.address)}</WeightMedium>
                                </TextColor>
                            </FontSize>
                        </FontRobotoSlab>
                    </TextCenter>
                    {detail.parties.length > 0 && (
                        <TextCenter>
                            <FontQuicksand>
                                <FontSize size={FrontFontSizes.SMALL} lineHeight="1.4">
                                    <TextColor color={FrontColors.NEUTRAL_18}>
                                        {detail.parties.map(formatPartyName).join(" & ")}
                                    </TextColor>
                                </FontSize>
                            </FontQuicksand>
                        </TextCenter>
                    )}
                </SpacingColumn>

                {/* Pipeline strip */}
                <SpacingColumn spacing={FrontSpacing.SMALL_3}>
                    <SectionLabel>Stage</SectionLabel>
                    <PipelineStrip current={detail.bucket} />
                </SpacingColumn>

                {/* Quick summary */}
                <SpacingColumn spacing={FrontSpacing.SMALL_3}>
                    <SectionLabel>What&apos;s happening</SectionLabel>
                    <Card>
                        {!summaryLoaded ? (
                            <FontQuicksand>
                                <FontSize size={FrontFontSizes.SMALL} lineHeight="1.4">
                                    <TextColor color={FrontColors.NEUTRAL_INK_LIGHT}>
                                        Loading the latest summary…
                                    </TextColor>
                                </FontSize>
                            </FontQuicksand>
                        ) : summary === null ? (
                            <FontQuicksand>
                                <FontSize size={FrontFontSizes.SMALL} lineHeight="1.4">
                                    <TextColor color={FrontColors.NEUTRAL_INK_LIGHT}>
                                        No summary yet — this case is brand new or hasn&apos;t had activity to summarise.
                                    </TextColor>
                                </FontSize>
                            </FontQuicksand>
                        ) : (
                            <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
                                <FontRobotoSlab>
                                    <FontSize size={FrontFontSizes.MED} lineHeight="1.4">
                                        <TextColor color={FrontColors.NEUTRAL_INK}>
                                            <WeightMedium>{summary.headline}</WeightMedium>
                                        </TextColor>
                                    </FontSize>
                                </FontRobotoSlab>
                                {summary.predicted_completion_month !== null && (
                                    <FontQuicksand>
                                        <FontSize size={FrontFontSizes.SMALL} lineHeight="1.4">
                                            <TextColor color={FrontColors.NEUTRAL_INK_LIGHT}>
                                                Predicted completion: <WeightBold>{summary.predicted_completion_month}</WeightBold>
                                                {summary.predicted_completion_confidence !== null && (
                                                    <> ({summary.predicted_completion_confidence} confidence)</>
                                                )}
                                            </TextColor>
                                        </FontSize>
                                    </FontQuicksand>
                                )}
                                {(summary.whats_next_v2 !== null ? summary.whats_next_v2.length > 0 : summary.whats_next.length > 0) && (
                                    <SpacingColumn spacing={FrontSpacing.SMALL_2}>
                                        <FontQuicksand>
                                            <FontSize size={FrontFontSizes.X_SMALL}>
                                                <TextColor color={FrontColors.NEUTRAL_INK_LIGHT}>
                                                    <Uppercase><WeightMedium>What&apos;s next</WeightMedium></Uppercase>
                                                </TextColor>
                                            </FontSize>
                                        </FontQuicksand>
                                        <SpacingColumn spacing={FrontSpacing.SMALL_1}>
                                            {(summary.whats_next_v2 !== null
                                                ? summary.whats_next_v2
                                                : summary.whats_next.map((action): TSummaryWhatsNextItem => ({ owner: null, action }))
                                            ).map((item, i) => (
                                                <FontQuicksand key={i}>
                                                    <FontSize size={FrontFontSizes.SMALL} lineHeight="1.45">
                                                        <TextColor color={FrontColors.NEUTRAL_INK}>
                                                            • {item.owner !== null && <WeightMedium>{summaryOwnerDisplay[item.owner]}: </WeightMedium>}
                                                            {item.action}
                                                        </TextColor>
                                                    </FontSize>
                                                </FontQuicksand>
                                            ))}
                                        </SpacingColumn>
                                    </SpacingColumn>
                                )}
                                {(summary.waiting_on.length > 0 || summary.blocking.length > 0) && (
                                    <SpacingColumn spacing={FrontSpacing.SMALL_2}>
                                        <FontQuicksand>
                                            <FontSize size={FrontFontSizes.X_SMALL}>
                                                <TextColor color={FrontColors.PERTINENT_CORAL_4}>
                                                    <Uppercase><WeightMedium>Waiting on</WeightMedium></Uppercase>
                                                </TextColor>
                                            </FontSize>
                                        </FontQuicksand>
                                        <SpacingColumn spacing={FrontSpacing.SMALL_1}>
                                            {summary.waiting_on.length > 0
                                                ? summaryOwnerOrder
                                                    .flatMap((owner) => summary.waiting_on.filter((item) => item.owner === owner))
                                                    .map((item, i) => (
                                                        <FontQuicksand key={i}>
                                                            <FontSize size={FrontFontSizes.SMALL} lineHeight="1.45">
                                                                <TextColor color={FrontColors.NEUTRAL_INK}>
                                                                    • <WeightMedium>{summaryOwnerDisplay[item.owner]}</WeightMedium> — {item.label}
                                                                </TextColor>
                                                            </FontSize>
                                                        </FontQuicksand>
                                                    ))
                                                : summary.blocking.map((item, i) => (
                                                    <FontQuicksand key={i}>
                                                        <FontSize size={FrontFontSizes.SMALL} lineHeight="1.45">
                                                            <TextColor color={FrontColors.NEUTRAL_INK}>
                                                                • {item}
                                                            </TextColor>
                                                        </FontSize>
                                                    </FontQuicksand>
                                                ))}
                                        </SpacingColumn>
                                    </SpacingColumn>
                                )}
                            </SpacingColumn>
                        )}
                    </Card>
                </SpacingColumn>

                {/* Marketing snapshot */}
                <SpacingColumn spacing={FrontSpacing.SMALL_3}>
                    <SectionLabel>Marketing</SectionLabel>
                    <SpacingRow spacing={FrontSpacing.SMALL_2} childSize="even-fill">
                        <StatTile
                            label="Asking price"
                            value={formatPounds(detail.asking_price_main_currency)}
                        />
                        <StatTile
                            label="Days on market"
                            value={detail.days_on_market === null ? "—" : `${detail.days_on_market}`}
                            helper={detail.live_date === null ? undefined : <>Listed {formatIsoDate(detail.live_date)}</>}
                        />
                        <StatTile
                            label="Offers (30d)"
                            value={detail.offers_count_last_30d}
                        />
                        <StatTile
                            label="Viewings (30d)"
                            value={detail.viewings_count_last_30d}
                        />
                    </SpacingRow>
                </SpacingColumn>

                {/* Conveyancing timeline */}
                <SpacingColumn spacing={FrontSpacing.SMALL_3}>
                    <SectionLabel>Conveyancing</SectionLabel>
                    <SpacingRow spacing={FrontSpacing.SMALL_2} childSize="even-fill">
                        <StatTile
                            label="Instructed"
                            value={formatIsoDate(detail.instructed_at)}
                        />
                        <StatTile
                            label="Expected exchange"
                            value={formatIsoDate(detail.expected_exchange_date)}
                        />
                        <StatTile
                            label="Expected completion"
                            value={formatIsoDate(detail.expected_completion_date)}
                        />
                        <StatTile
                            label="Completed"
                            value={formatIsoDate(detail.completed_at)}
                        />
                    </SpacingRow>
                </SpacingColumn>

                {/* Upload */}
                <SpacingColumn spacing={FrontSpacing.SMALL_3}>
                    <SectionLabel>Send us a document</SectionLabel>
                    {/* Suppressed in staff "view as introducer" preview — the
                        backend also rejects staff sessions at
                        requireLayUserSession, so this is belt-and-braces. */}
                    <DocumentUploader caseId={detail.id} disabled={previewBannerName !== null} />
                </SpacingColumn>

                {/* Stalling notice */}
                {detail.is_stalled && (
                    <Background color={FrontColors.PERTINENT_CORAL_21} borderRadius="5px">
                        <Padding type="custom" spacing={`${FrontSpacing.SMALL_3} ${FrontSpacing.MEDIUM_1}`}>
                            <SpacingColumn spacing={FrontSpacing.SMALL_1}>
                                <FontRobotoSlab>
                                    <FontSize size={FrontFontSizes.MED} lineHeight="1.3">
                                        <TextColor color={FrontColors.NEUTRAL_INK}>
                                            <WeightMedium>This case is stalling.</WeightMedium>
                                        </TextColor>
                                    </FontSize>
                                </FontRobotoSlab>
                                <FontQuicksand>
                                    <FontSize size={FrontFontSizes.X_SMALL} lineHeight="1.4">
                                        <TextColor color={FrontColors.NEUTRAL_INK}>
                                            No activity in the last 12 weeks ({detail.dead_time_days} days idle).
                                        </TextColor>
                                    </FontSize>
                                </FontQuicksand>
                            </SpacingColumn>
                        </Padding>
                    </Background>
                )}
            </SpacingColumn>
        </PurpleShell>
    );
};

export default IntroducerPortalCaseDetailPage;
